import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';

export function PortalForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/portal/auth/forgot-password', { email });
    },
    onSuccess: () => setSubmitted(true),
    // Always show success even on error (no email enumeration)
    onError: () => setSubmitted(true),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    mutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-10 w-auto" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-text-primary">Vendor Security Portal</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Powered by VeriGRC</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {submitted ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-accent-emerald" />
              <p className="text-base font-medium text-text-primary">Check your email</p>
              <p className="text-sm text-text-secondary">
                If an account exists for <span className="font-medium text-text-primary">{email}</span>,
                you'll receive a password reset link within a few minutes.
              </p>
              <Link to="/portal/login" className="mt-2 text-sm text-accent-cyan hover:underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-text-primary">Reset your password</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email">Email address</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="you@vendor.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.isPending || !email}
                >
                  {mutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
                  ) : (
                    'Send reset link'
                  )}
                </Button>

                <div className="text-center">
                  <Link to="/portal/login" className="text-sm text-text-secondary hover:text-text-primary">
                    Back to sign in
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
